/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable max-lines */
/* eslint-disable react-perf/jsx-no-new-object-as-prop */
import React, { FormEvent, useCallback, useState } from 'react'
import {
  Box, Modal, FloatingInput,
} from '@usap/uicomponents'
import { useLocationDispatch } from '../../../../contexts/location.context'
import {
  ShippingFormContainer,
  ShippingModalContainer,
  ShippingSubmit,
  StyledH3,
  StyledP,
  ShippingFormFootnotes,
  StyledHeaderContainer,
  ErrorMessage,
  FormAndMessageContainer,
} from './SummaryShippingModal.css'
import { getCityDetails } from '../../../../helpers'
import useUpdateCartItemsShippingDetails from '../../../../helpers/updateCartItemsShippingDetails'

type SummaryShippingModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  city: string
  zipcode: string
}

type SummaryShippingModalContentProps = {
  onOpenChange: (open: boolean) => void
  zipcode: string
}

const ZIP_INPUT = 'shippingZipcodeInput'

// only 5 digits
const isValidZip = (zipCode: string) => /^\d{5}?$/.test(zipCode)
const INVALID_ZIP_MESSAGE = 'Provide a valid zip code'

const SummaryShippingModalContent: React.FC<SummaryShippingModalContentProps> = ({ onOpenChange, zipcode }) => {
  const [isInputEmpty, setIsInputEmpty] = useState(false)

  const updateCartModifiers = useUpdateCartItemsShippingDetails()
  const setLocation = useLocationDispatch()
  const [inputValue, setInputValue] = useState(zipcode || '')
  const [zipCodeError, setZipCodeError] = useState('')

  const handleChangeZip = useCallback(
    event => {
      const { value } = event.target
      if (zipCodeError !== '' && value === '') setZipCodeError('')

      setInputValue(value)
    },
    [zipCodeError],
  )

  const submitHandler = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!isValidZip(inputValue)) {
      setZipCodeError(INVALID_ZIP_MESSAGE)
      return
    }

    try {
      const newData = await getCityDetails(inputValue)

      await updateCartModifiers({
        location: {
          zipcode: inputValue,
          state: newData.region,
          city: newData.city,
        },
      })
      setLocation({
        postalCode: inputValue,
        city: newData.city,
        state: newData.region,
      })

      if (__BROWSER__ && localStorage) {
        localStorage.setItem('postalCode', inputValue.trim())
      }

      onOpenChange(false)
    } catch (err) {
      setZipCodeError(INVALID_ZIP_MESSAGE)
    }
  }, [inputValue, onOpenChange, setLocation, updateCartModifiers])

  const handleInput = useCallback(event => {
    const value = (event?.target?.value || '').trim()
    if (event.target.name === ZIP_INPUT) {
      setIsInputEmpty(value === '')
    }

    if (isValidZip(value)) {
      setZipCodeError('')
    } else {
      setZipCodeError(INVALID_ZIP_MESSAGE)
    }
  }, [setIsInputEmpty, setZipCodeError])

  const onKeyUp = useCallback((event: any) => {
    if (event?.keyCode === 13) {
      submitHandler(event)
    }
  }, [submitHandler])

  return (
    <ShippingModalContainer>
      <Box>
        <form onSubmit={submitHandler} onInput={handleInput}>
          <FormAndMessageContainer>
            <ShippingFormContainer>
              <FloatingInput
                label="Zip Code"
                name={ZIP_INPUT}
                defaultValue={zipcode}
                onChange={handleChangeZip}
                onKeyUp={onKeyUp}
                isCheckOutSignInWmo
                hasError={Boolean(zipCodeError)}
                isWmoEnabled
              />

              <ShippingSubmit
                type="submit"
                disabled={Boolean(zipCodeError || isInputEmpty)}
              >
                Update
              </ShippingSubmit>
            </ShippingFormContainer>
            {zipCodeError !== '' && (
              <ErrorMessage>{zipCodeError}</ErrorMessage>
            )}
          </FormAndMessageContainer>
        </form>
      </Box>
      <Box>
        <ShippingFormFootnotes>
          † Shipping calculator is not available for Alaska, Hawaii and other countries.
        </ShippingFormFootnotes>
      </Box>
    </ShippingModalContainer>
  )
}

// eslint-disable-next-line arrow-body-style
const SummaryShippingModal: React.FC<SummaryShippingModalProps> = ({
  open, onOpenChange, city, zipcode,
}) => (
  <Modal
    open={open}
    onOpenChange={onOpenChange}
    content={<SummaryShippingModalContent onOpenChange={onOpenChange} zipcode={zipcode} />}
    header={(
      <StyledHeaderContainer>
        <StyledH3>
          <span>{'Shipping to '}</span>
          <span translate="no">{`${city}, ${zipcode}`}</span>
        </StyledH3>
        <StyledP style={{ marginTop: '0px' }}>Enter your ZIP code for the most accurate options †</StyledP>
      </StyledHeaderContainer>
)}
  />
)

export default SummaryShippingModal
